import type {
	BillingInvoiceStatus,
	BillingMoney,
	BillingPaymentStatus,
	BillingProviderName,
	BillingSubscriptionStatus,
} from "../types.ts";

export interface CanonicalMoney {
	currency: string;
	value: string;
}

export interface CanonicalPayment {
	kind: "payment";
	provider: BillingProviderName;
	providerPaymentId: string;
	providerCustomerId?: string | null;
	providerProfileId?: string | null;
	providerSubscriptionId?: string | null;
	providerPaymentLinkId?: string | null;
	status: BillingPaymentStatus;
	amount: BillingMoney;
	amountRefunded?: BillingMoney | null;
	description?: string | null;
	metadata: unknown;
	paidAt?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
	raw: unknown;
}

export interface CanonicalSubscription {
	kind: "subscription";
	provider: BillingProviderName;
	providerSubscriptionId: string;
	providerCustomerId: string;
	providerProfileId?: string | null;
	status: BillingSubscriptionStatus;
	amount?: BillingMoney | null;
	currency?: string | null;
	interval?: string | null;
	description?: string | null;
	metadata: unknown;
	nextPaymentDate?: string | null;
	createdAt?: string | null;
	canceledAt?: string | null;
	updatedAt?: string | null;
	raw: unknown;
}

export interface CanonicalInvoice {
	kind: "invoice";
	provider: BillingProviderName;
	providerInvoiceId: string;
	providerProfileId?: string | null;
	providerCustomerId?: string | null;
	status: BillingInvoiceStatus;
	amount?: BillingMoney | null;
	amountPaid?: BillingMoney | null;
	description?: string | null;
	metadata: unknown;
	issuedAt?: string | null;
	paidAt?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
	raw: unknown;
}

export type CanonicalBillingDocument =
	| CanonicalPayment
	| CanonicalSubscription
	| CanonicalInvoice;

export function canonicalDocumentSubjectId(
	document: CanonicalBillingDocument,
): string {
	if (document.kind === "payment") {
		return document.providerPaymentId;
	}
	if (document.kind === "subscription") {
		return document.providerSubscriptionId;
	}
	return document.providerInvoiceId;
}

export function canonicalDocumentRevision(document: CanonicalBillingDocument): {
	providerUpdatedAt?: Date;
	sequence?: string;
} {
	const stamp =
		document.updatedAt ??
		(document.kind === "payment"
			? document.paidAt ?? document.createdAt
			: document.kind === "subscription"
				? document.canceledAt ?? document.createdAt
				: document.paidAt ?? document.createdAt);
	return {
		sequence: `${document.kind}:${canonicalDocumentSubjectId(document)}:${documentFingerprint(document)}`,
		...(stamp ? { providerUpdatedAt: new Date(stamp) } : {}),
	};
}

function documentFingerprint(document: CanonicalBillingDocument): string {
	const amount =
		document.kind === "payment"
			? `${document.amount.currency}:${document.amount.value}`
			: `${document.amount?.currency ?? ""}:${document.amount?.value ?? ""}`;
	const refunded =
		document.kind === "payment"
			? `${document.amountRefunded?.currency ?? ""}:${document.amountRefunded?.value ?? ""}`
			: "";
	return [
		document.status,
		amount,
		refunded,
		document.updatedAt ?? "",
		document.kind === "payment"
			? (document.paidAt ?? "")
			: document.kind === "subscription"
				? (document.canceledAt ?? "")
				: (document.paidAt ?? ""),
	].join(":");
}
